import { DeviceModel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';

import { FirmwareUpdateRequest } from './firmware-update-requests';

export enum ScheduledJobType {
	FirmwareUpdate = 'FIRMWARE_UPDATE',
	Reboot = 'REBOOT'
}

export enum ScheduledJobStatus {
	Cancelled = 'CANCELLED',
	Completed = 'COMPLETED',
	Executing = 'EXECUTING',
	Failed = 'FAILED',
	Missed = 'MISSED',
	Pending = 'PENDING'
}

export interface ScheduledJob {
	id: string;
	jobId: string;
	name: string;
	type: ScheduledJobType;
	status: ScheduledJobStatus;
	requestCount: number;
	executeAt: Date;
	createdBy: string;
	createdAt: Date;
	updatedBy: string;
	updatedAt: Date;
}

export interface CreateScheduledJobData {
	deviceIds: string[];
	jobType: ScheduledJobType;
}
export interface ScheduledJobRequestBase {
	id: string;
	type: ScheduledJobType;
	status: ScheduledJobStatus;
	jobId: string;
	executeAt: string;
}

export interface ScheduledJobRequestDevice {
	known: boolean;
	nodeId?: string;
	hardwareId: string;
	model: DeviceModel;
	status: DeviceStatus;
	name?: string;
	roomName?: string;
	currentFirmwareVersion?: string;
}

export interface FirmwareUpdateJobRequest extends ScheduledJobRequestBase {
	type: ScheduledJobType.FirmwareUpdate;
	task: {
		packageKey: string;
		packageVersion: string;
		hosted: boolean;
		device: ScheduledJobRequestDevice;
	};
	result?: FirmwareUpdateRequest;
}

export interface RebootJobRequest extends ScheduledJobRequestBase {
	type: ScheduledJobType.Reboot;
	task: {
		device: ScheduledJobRequestDevice;
	};
}

// this will be extended as a union to support other types of requests such as reboot
// e.g.  export type ScheduledJobRequest = FirmwareUpdateJobRequest | RebootJobRequest;
export type ScheduledJobRequest = FirmwareUpdateJobRequest | RebootJobRequest;

export interface DeviceScheduledJobFeature {
	firmwareUpdateJobRequests: {
		pendingPrimary?: FirmwareUpdateJobRequest;
		pendingHosted?: FirmwareUpdateJobRequest;
	};
	rebootJobRequests: {
		pending?: RebootJobRequest;
	};
}

type DeviceWithScheduledJobs = { features: { scheduledJobs?: DeviceScheduledJobFeature } };

/**
 * Given an array of 1 or more devices, parses the scheduledJobs pending job requests for firmware updates and reboots, and returns the
 * next common pending job information by job type if all devices share the same pending job.
 * For firmware update job, if both primary and hosted firmware updates exist, take the one scheduled next.
 * @param devices an array of devices, with the features.scheduledJobs attribute.
 * @returns an array of the next common pending job(s) for the given devices, which may include a firmware update job and/or a reboot job.
 */
export function getCommonUpcomingScheduledJobs(
	devices: DeviceWithScheduledJobs[]
): (FirmwareUpdateJobRequest | RebootJobRequest)[] {
	// Get all pending requests for each type of job across all devices
	const allPendingPrimary = getFirmwareUpdateRequests(devices, (requests) => requests.pendingPrimary);
	const allPendingHosted = getFirmwareUpdateRequests(devices, (requests) => requests.pendingHosted);
	const allPendingReboot = getRebootRequests(devices, (requests) => requests.pending);

	// Determine the common pending requests for each type of job across all devices
	const pendingPrimary = getCommonRequest(allPendingPrimary, devices);
	const pendingHosted = getCommonRequest(allPendingHosted, devices);
	const pendingReboot = getCommonRequest(allPendingReboot, devices);

	// If both primary and hosted firmware updates exist, take next scheduled one
	let nextFirmwareJob: FirmwareUpdateJobRequest | undefined;
	if (pendingPrimary && pendingHosted) {
		nextFirmwareJob = pendingPrimary.executeAt <= pendingHosted.executeAt ? pendingPrimary : pendingHosted;
	} else {
		nextFirmwareJob = pendingPrimary ?? pendingHosted;
	}

	return [nextFirmwareJob, pendingReboot].filter((job) => job !== undefined);
}

// local helper functions for getCommonScheduledJobs
const getFirmwareUpdateRequests = (
	devices: DeviceWithScheduledJobs[],
	selector: (requests: DeviceScheduledJobFeature['firmwareUpdateJobRequests']) => FirmwareUpdateJobRequest | undefined
): FirmwareUpdateJobRequest[] =>
	devices
		.map((device) => selector(device.features.scheduledJobs?.firmwareUpdateJobRequests ?? {}))
		.filter((request): request is FirmwareUpdateJobRequest => request !== undefined);

const getRebootRequests = (
	devices: DeviceWithScheduledJobs[],
	selector: (requests: DeviceScheduledJobFeature['rebootJobRequests']) => RebootJobRequest | undefined
): RebootJobRequest[] =>
	devices
		.map((device) => selector(device.features.scheduledJobs?.rebootJobRequests ?? {}))
		.filter((request): request is RebootJobRequest => request !== undefined);

const getCommonRequest = <T extends ScheduledJobRequest>(
	requests: T[],
	devices: DeviceWithScheduledJobs[]
): T | undefined => {
	const requestsByJobId = new Map(requests.map((request) => [request.jobId, request]));
	return requests.length === devices.length && requestsByJobId.size === 1 ? requests[0] : undefined;
};
